// Noticeboard — UI types

export type NoticePriority = 'urgent' | 'high' | 'normal' | 'low';
export type NoticeType = 'general' | 'academic' | 'event' | 'emergency' | 'fee_update';
export type NoticeStatus = 'scheduled' | 'active' | 'expired';

export interface NoticeItem {
    id: string;
    title: string;
    content: string | null;
    type: NoticeType;
    priority: NoticePriority;
    is_school_wide: boolean;
    requires_acknowledgment: boolean;
    published_at: string | null;
    expires_at: string | null;
    created_at: string;
    /** Derived audiences display string (e.g. "Class 10, Class 11-A") */
    audience_display: string;
    /** Derived status from timestamps */
    status: NoticeStatus;
    /** Acknowledgment tracking */
    acknowledgments_count: number;
    total_audience_count: number;
    /** Raw audience entries for editing */
    audiences: { class_id: string; section_id: string | null; class_name?: string; section_name?: string }[];
}

export interface AcknowledgmentEntry {
    id: string;
    user_id: string;
    user_name: string;
    user_role: string;
    avatar_initials: string;
    acknowledged_at: string | null;
}

/**
 * Compute derived status from notice timestamps.
 */
export function computeNoticeStatus(publishedAt: string | null, expiresAt: string | null): NoticeStatus {
    const now = new Date();

    if (publishedAt) {
        const pubDate = new Date(publishedAt);
        if (pubDate > now) return 'scheduled';
    }

    if (expiresAt) {
        const expDate = new Date(expiresAt);
        if (expDate < now) return 'expired';
    }

    return 'active';
}

/**
 * Build human-readable audience display string from audience records.
 */
export function buildAudienceDisplay(
    isSchoolWide: boolean,
    audiences: { class_name?: string; section_name?: string }[]
): string {
    if (isSchoolWide) return 'School-Wide';
    if (audiences.length === 0) return '—';

    return audiences
        .map((a) => {
            if (a.section_name) return `${a.class_name || 'Class'}-${a.section_name}`;
            return a.class_name || 'Class';
        })
        .join(', ');
}
